import { useHydrated } from '@tanstack/react-router'
import type { RefObject } from 'react'
import { useEffect, useRef } from 'react'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'
import { createFloraScrollTimeline } from '@/lib/gsap/flora-scroll-timeline'
import type { SceneEngine } from '@/lib/three/scene-engine'
import { useSceneStore } from '@/stores/scene-store'

export interface FloraCanvasModelProps {
  scrollContainerRef: RefObject<HTMLDivElement | null>
}

const BOOT_FALLBACK_DELAY_MS = 2500
const READY_WATCHDOG_DELAY_MS = 10000
const BOOT_INTERACTION_EVENTS = [
  'scroll',
  'wheel',
  'pointerdown',
  'touchstart',
  'keydown',
] as const

export function useFloraCanvasModel({
  scrollContainerRef,
}: FloraCanvasModelProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const setRawProgress = useSceneStore((state) => state.setRawProgress)
  const setSceneReady = useSceneStore((state) => state.setSceneReady)
  const isSceneReady = useSceneStore((state) => state.sceneReady)
  const reducedMotion = usePrefersReducedMotion()
  const isHydrated = useHydrated()

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    const canvasElement = canvasRef.current
    if (!isHydrated || !scrollContainer || !canvasElement) return
    const canvas: HTMLCanvasElement = canvasElement

    let disposed = false
    let booted = false
    let engine: SceneEngine | null = null
    let pendingProgress: number | null = null
    let readyWatchdogId = 0

    const cleanupTimeline = createFloraScrollTimeline({
      scrollContainer,
      immediate: reducedMotion,
      onRawProgress(progress) {
        setRawProgress(progress)
      },
      onSmoothedProgress(progress) {
        if (engine) {
          engine.setProgress(progress)
          return
        }
        pendingProgress = progress
      },
    })

    function removeBootTriggers(): void {
      for (const eventName of BOOT_INTERACTION_EVENTS) {
        window.removeEventListener(eventName, bootEngine)
      }
      window.clearTimeout(bootFallbackId)
    }

    function bootEngine(): void {
      if (booted || disposed) return
      booted = true
      removeBootTriggers()
      readyWatchdogId = window.setTimeout(() => {
        if (!disposed) {
          setSceneReady(true)
        }
      }, READY_WATCHDOG_DELAY_MS)
      import('@/lib/three/scene-engine')
        .then(({ createSceneEngine }) => {
          if (disposed) return
          return createSceneEngine(canvas, { reducedMotion }).then(
            (createdEngine) => {
              if (disposed) {
                createdEngine.dispose()
                return
              }
              engine = createdEngine
              engine.onReady(() => {
                window.clearTimeout(readyWatchdogId)
                setSceneReady(true)
              })
              if (pendingProgress !== null) {
                engine.setProgress(pendingProgress)
              }
            },
          )
        })
        .catch(() => {
          if (!disposed) {
            setSceneReady(true)
          }
        })
    }

    for (const eventName of BOOT_INTERACTION_EVENTS) {
      window.addEventListener(eventName, bootEngine, {
        passive: true,
        once: true,
      })
    }
    const bootFallbackId = window.setTimeout(bootEngine, BOOT_FALLBACK_DELAY_MS)

    function handleResize(): void {
      engine?.resize()
    }
    window.addEventListener('resize', handleResize)

    return () => {
      disposed = true
      removeBootTriggers()
      window.clearTimeout(readyWatchdogId)
      window.removeEventListener('resize', handleResize)
      cleanupTimeline()
      engine?.dispose()
    }
  }, [
    scrollContainerRef,
    reducedMotion,
    setRawProgress,
    setSceneReady,
    isHydrated,
  ])

  return { canvasRef, isSceneReady }
}
